import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { clsx } from "clsx";
import ProductAccordion from "../ProductAccordion/ProductAccordion";
import ProductFindInStoreModal from "../ProductFindInStoreModal";
import ProductGallery from "@src/stories/ecommerce/Product/ProductGallery/ProductGallery";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import shopify from "frontend/js/shopify";
import ProductPrice from "../ProductPrice";
import ProductSwatches from "../ProductSwatches/ProductSwatches";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "ajax-cart-product-form": React.HTMLAttributes<HTMLElement>;
    }
  }
}

const SHIPPING_CONTENT = `<strong>SHIPPING</strong><br/>All Shipping costs are in local currency and will be calculated at checkout.<br/><br/><strong>30 DAY RETURNS</strong><br/>Refund available for full price items within 30 days, flat fee of USD $25 applies.`;

const labelStyle: React.CSSProperties = {
  color: "#54565A",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "19px",
  letterSpacing: "1.04px",
  textTransform: "uppercase",
};

interface QuickViewModalHeaderProps {
  productHandle: string;
  close: () => void;
  learnMoreLabel: string;
  findInStoreShowing: boolean
}

const QuickViewModalHeader: React.FC<QuickViewModalHeaderProps> = ({ productHandle, close, learnMoreLabel, findInStoreShowing }) => (
  <div className={`w-full flex items-center justify-end gap-[24px] px-5 lg:px-0 sticky top-0 left-0 bg-white ${!findInStoreShowing && 'z-10'}`}>
    <a
      href={`/products/${productHandle}`}
      className="flex items-center gap-2 no-underline"
      style={labelStyle}
    >
      {learnMoreLabel}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path d="M11 1.5H14.5V5M13.75 2.25L10 6M8.5 2.5H4C3.60218 2.5 3.22064 2.65804 2.93934 2.93934C2.65804 3.22064 2.5 3.60218 2.5 4V12C2.5 12.3978 2.65804 12.7794 2.93934 13.0607C3.22064 13.342 3.60218 13.5 4 13.5H12C12.3978 13.5 12.7794 13.342 13.0607 13.0607C13.342 12.7794 13.5 12.3978 13.5 12V7.5" stroke="#00263E" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </a>
    <button
      type="button"
      onClick={close}
      className="flex items-center gap-2 bg-transparent border-0 cursor-pointer p-0 items-center w-fit"
      style={labelStyle}
      aria-label="Close Quick View"
    >
      <span>
        Close
      </span>
      <div className="w-5 h-5 rounded-full bg-[#00263E] flex items-center justify-center flex-shrink-0 text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path d="M11.5623 2.43849C11.4223 2.2985 11.1955 2.2985 11.0556 2.43849L7.00065 6.49339L2.94575 2.43849C2.80575 2.2985 2.57896 2.2985 2.43898 2.43849C2.299 2.57848 2.29899 2.80528 2.43898 2.94526L6.49388 7.00016L2.43898 11.0551C2.29899 11.1951 2.29899 11.4219 2.43898 11.5618C2.57897 11.7018 2.80576 11.7018 2.94575 11.5618L7.00065 7.50693L11.0556 11.5618C11.1955 11.7018 11.4223 11.7018 11.5623 11.5618C11.7023 11.4218 11.7023 11.1951 11.5623 11.0551L7.50742 7.00016L11.5623 2.94526C11.7023 2.80527 11.7023 2.57847 11.5623 2.43849Z" fill="white"/>
        </svg>
      </div>
    </button>
  </div>
);


interface QuickViewVariantPickerProps {
  restProduct: any;
  selectedOptions: Record<string, string>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedVariant: any;
  findInStoreSettings: any;
  isSizeSelectorOpen: boolean;
  setIsSizeSelectorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  combinedStoreInventory: Record<number, boolean> | null;
}

const QuickViewVariantPicker: React.FC<QuickViewVariantPickerProps & { onFindInStoreOpenChange?: (open: boolean) => void }> = ({
  restProduct,
  selectedOptions,
  setSelectedOptions,
  selectedVariant,
  findInStoreSettings,
  isSizeSelectorOpen,
  setIsSizeSelectorOpen,
  combinedStoreInventory,
  onFindInStoreOpenChange,
}) => {
  if (restProduct.has_only_default_variant) return null;

  const sizeOptions = (restProduct.options as any[])
    .map((option: any, optionIndex: number) => ({ option, optionIndex }))
    .filter(({ optionIndex }) => optionIndex > 0);

  const variantOptionFieldsets = sizeOptions.map(({ option, optionIndex }) => (
    <div key={option.name} className="variant-radios mb-4">
      <fieldset
        data-prodify-option-container
        name={option.name}
      >
        <legend className="bc mb-2 text-[#54565A]">{option.name}</legend>
        <div className="pdp-options flex flex-wrap">
          {(option.values as string[]).map((value) => {
            const variantOptionKey = `option${optionIndex + 1}`;
            const isAvailable = (restProduct.variants as any[]).some(
              (variant) => variant[variantOptionKey] === value && (
                combinedStoreInventory
                  ? !!combinedStoreInventory[variant.id]
                  : variant.available
              )
            );
            const isSelected = selectedOptions[option.name] === value;
            const inputId = `qv-${option.name}-${value}`;
            return (
              <React.Fragment key={value}>
                <input
                  type="radio"
                  id={inputId}
                  data-prodify-variant-radio
                  name={`quickview-${option.name}`}
                  value={value}
                  checked={isSelected}
                  disabled={!isAvailable}
                  className="sr-only"
                  onChange={() => {
                    if (!isAvailable) return;
                    setSelectedOptions((previousOptions) => ({ ...previousOptions, [option.name]: value }));
                  }}
                />
                <label
                  htmlFor={inputId}
                  data-unavailable={!isAvailable ? "true" : undefined}
                  className={clsx(
                    "b3 font-medium cursor-pointer",
                    isSelected ? "bg-[#00263E] text-white" : "bg-[#ECEBEB] text-[#00263E]",
                    !isAvailable && "opacity-40 cursor-not-allowed"
                  )}
                  style={{ padding: "10px 20px", margin: "4px" }}
                >
                  {value}
                </label>
              </React.Fragment>
            );
          })}
        </div>
      </fieldset>
    </div>
  ));

  const addToCartForm = (
    <ajax-cart-product-form>
      <form
        action="/cart/add"
        method="post"
        id="product-form-quickview"
        className="product-form"
        data-prodify-product-form
      >
        <input type="hidden" name="id" value={selectedVariant?.id ?? ""} />
        <input type="hidden" name="quantity" value="1" />
        <div className="flex flex-row w-full">
          <button
            type="submit"
            name="add"
            data-add-to-cart="default"
            className="btn-primary cursor-pointer flex-auto"
            disabled={!selectedVariant}
          >
            <span className="[[processing]_&]:hidden flex flex-row items-center justify-center">
              {selectedVariant ? "Add to Bag" : "Select a Size"}
            </span>
            <span className="hidden [[processing]_&]:flex flex-row items-center justify-center">
              Adding...
            </span>
          </button>
        </div>
        <div data-ajax-cart-errors="form" />
      </form>
    </ajax-cart-product-form>
  );

  return (
    <>
      <div className="hidden lg:block">
        {variantOptionFieldsets}
        <div className="flex justify-between items-center">
          <div>
            {findInStoreSettings && (
              <ProductFindInStoreModal settings={findInStoreSettings} onOpenChange={onFindInStoreOpenChange} />
            )}
          </div>
        </div>
        <div className="pdp-add-to-cart mt-2 mb-4">{addToCartForm}</div>
      </div>

      <div className="lg:hidden">
        {!isSizeSelectorOpen ? (
          <button
            className="button btn-primary w-full"
            onClick={() => setIsSizeSelectorOpen(true)}
          >
            Select A Size
          </button>
        ) : (
          <div className={clsx("size-selector-container flex flex-col items-center gap-4 p-4", isSizeSelectorOpen && "open")}>
            <h4>Select Size</h4>
            <div className="size-grid w-full">{variantOptionFieldsets}</div>
            <div className="flex w-full gap-2">
              <button
                className="button btn-outline !w-fit"
                onClick={() => setIsSizeSelectorOpen(false)}
              >
                <i className="icon-chevron-left text-lg" />
              </button>
              <div className="pdp-add-to-cart w-full">{addToCartForm}</div>
            </div>
          </div>
        )}
        <div>
          {findInStoreSettings && (
            <ProductFindInStoreModal settings={findInStoreSettings} onOpenChange={onFindInStoreOpenChange} />
          )}
        </div>
      </div>
    </>
  );
};


interface QuickViewProductDetailsProps {
  restProduct: any;
  productTag: string | null;
  metafields: { modelWears?: string; atfStatement?: string };
  descriptionHtml: string;
  swatches: any[];
  currentColor: string;
  selectedOptions: Record<string, string>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedVariant: any;
  findInStoreSettings: any;
  isSizeSelectorOpen: boolean;
  setIsSizeSelectorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  accordionItems: Array<{ title: string; content: string }>;
  combinedStoreInventory: Record<number, boolean> | null;
}

const QuickViewProductDetails: React.FC<QuickViewProductDetailsProps & { onFindInStoreOpenChange?: (open: boolean) => void }> = ({
  restProduct,
  productTag,
  metafields,
  descriptionHtml,
  swatches,
  currentColor,
  selectedOptions,
  setSelectedOptions,
  selectedVariant,
  findInStoreSettings,
  isSizeSelectorOpen,
  setIsSizeSelectorOpen,
  accordionItems,
  combinedStoreInventory,
  onFindInStoreOpenChange,
}) => (
  <div className="pdp-details flex flex-col p-4 lg:p-0 pb-12 lg:sticky lg:top-12 flex-1">

    <div className="flex flex-row lg:flex-col gap-4 items-start justify-between lg:items-start order-1 max-lg:mb-4">
      <h2 className="text__heading max-lg:max-w-2/3">{restProduct.title}</h2>
      {productTag && (
        <span className="bc text-[#BBBBBD] lg:text-[#54565A] text-right block">{productTag}</span>
      )}
    </div>

    <div className="pdp-description b3 my-[16px] order-4 lg:order-2">
      <span
        dangerouslySetInnerHTML={{
          __html: metafields.atfStatement || descriptionHtml || "",
        }}
      />
      {" "}
      <button
        type="button"
        className="hidden lg:inline view-more-details w-auto text-[#121212] font-normal normal-case min-h-[auto] underline-offset-2 tracking-normal underline text-sm hover:text-black transition-colors cursor-pointer bg-transparent border-none p-0"
        style={{ minHeight: 0, height: "auto" }}
        onClick={() => {
          const headers = document.querySelectorAll(".accordion__header");
          headers.forEach((header) => {
            const title = header.querySelector(".accordion__title");
            if (title?.textContent?.trim().toLowerCase().includes("details")) {
              const content = header.closest(".accordion")?.querySelector(".accordion-content");
              if (content && !content.classList.contains("active")) {
                (header as HTMLElement).click();
              }
              setTimeout(() => {
                const accordion = header.closest(".accordion");
                if (accordion) {
                  let scrollableParent = accordion.parentElement;
                  while (scrollableParent && scrollableParent !== document.body && scrollableParent.scrollHeight <= scrollableParent.clientHeight) {
                    scrollableParent = scrollableParent.parentElement;
                  }
                  if (scrollableParent && scrollableParent !== document.body) {
                    scrollableParent.scrollTo({
                      top: scrollableParent.scrollHeight,
                      behavior: "smooth"
                    });
                  } else {
                    accordion.scrollIntoView({ behavior: "smooth", block: "end" });
                  }
                }
              }, 200);
            }
          });
        }}
      >
        View more
      </button>
    </div>

    <div className="flex flex-col gap-4 mb-4 order-2 lg:order-3">
      <ProductPrice product={restProduct} templateName="product" />
    </div>

    <div className="flex flex-col-reverse lg:flex-col gap-4 order-3 lg:order-4">
      <div className="flex justify-between my-4">
        {swatches.length > 0 && (
          <>
            <div className="bc uppercase text-[#54565A]">Colour</div>
            <div className="justify-end product__swatch-container">
              <ProductSwatches swatches={swatches} currentColor={currentColor} />
            </div>
          </>
        )}
      </div>

      <QuickViewVariantPicker
        restProduct={restProduct}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        selectedVariant={selectedVariant}
        findInStoreSettings={findInStoreSettings}
        isSizeSelectorOpen={isSizeSelectorOpen}
        setIsSizeSelectorOpen={setIsSizeSelectorOpen}
        combinedStoreInventory={combinedStoreInventory}
        onFindInStoreOpenChange={onFindInStoreOpenChange}
      />
    </div>

    <div className="mt-2 order-5">
      <ProductAccordion
        items={accordionItems}
        show_header={false}
      />
    </div>
  </div>
);


interface QuickViewContentProps {
  productHandle: string;
  close: () => void;
  galleryImages: Array<{ url: string; alt: string }>;
  galleryContainerRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  restProduct: any;
  productTag: string | null;
  metafields: { modelWears?: string; atfStatement?: string };
  descriptionHtml: string;
  swatches: any[];
  currentColor: string;
  selectedOptions: Record<string, string>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedVariant: any;
  findInStoreSettings: any;
  isSizeSelectorOpen: boolean;
  setIsSizeSelectorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  accordionItems: Array<{ title: string; content: string }>;
  learnMoreLabel: string;
  combinedStoreInventory: Record<number, boolean> | null;
}

const QuickViewContent: React.FC<QuickViewContentProps> = ({
  productHandle,
  close,
  galleryImages,
  galleryContainerRef,
  isMobile,
  restProduct,
  productTag,
  metafields,
  descriptionHtml,
  swatches,
  currentColor,
  selectedOptions,
  setSelectedOptions,
  selectedVariant,
  findInStoreSettings,
  isSizeSelectorOpen,
  setIsSizeSelectorOpen,
  accordionItems,
  learnMoreLabel,
  combinedStoreInventory,
}) => {
  const [findInStoreShowing, setFindInStoreShowing] = React.useState(false);
  return (
    <>
      <QuickViewModalHeader
        productHandle={productHandle}
        close={close}
        learnMoreLabel={learnMoreLabel}
        findInStoreShowing={findInStoreShowing}
      />
      <div className="flex flex-col lg:flex-row pdp-main lg:gap-4 2xl:gap-[60px] relative pb-12">
        <div className="media js-required w-full lg:w-[55%] lg:flex-shrink-0">
          <div className="product-gallery" ref={galleryContainerRef} key={productHandle}>
            <ProductGallery
              images={galleryImages}
              galleryType={isMobile ? "carousel" : "tiled"}
              videoUrl={undefined}
              isGiftCard={false}
              onImageClick={undefined}
            />
          </div>
        </div>
        <QuickViewProductDetails
          restProduct={restProduct}
          productTag={productTag}
          metafields={metafields}
          descriptionHtml={descriptionHtml}
          swatches={swatches}
          currentColor={currentColor}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          selectedVariant={selectedVariant}
          findInStoreSettings={findInStoreSettings}
          isSizeSelectorOpen={isSizeSelectorOpen}
          setIsSizeSelectorOpen={setIsSizeSelectorOpen}
          accordionItems={accordionItems}
          combinedStoreInventory={combinedStoreInventory}
          onFindInStoreOpenChange={setFindInStoreShowing}
        />
      </div>
    </>
  );
};

let cachedSettings: any = null;

export const QuickViewModal: React.FC<{ settings?: any }> = ({ settings: rawSettings = {} }) => {
  if (rawSettings?.additional_content_2 || rawSettings?.additional_content_3) {
    cachedSettings = rawSettings;
  }
  const settings = cachedSettings || rawSettings;
  const isMobile = useMobileBreakpoint("1024px");
  const [isOpen, setIsOpen] = useState(false);
  const [productHandle, setProductHandle] = useState("");
  const [loadingTitle, setLoadingTitle] = useState("");
  const [restProduct, setRestProduct] = useState<any>(null);
  const [galleryImages, setGalleryImages] = useState<Array<{ url: string; alt: string }>>([]);
  const [metafields, setMetafields] = useState<{
    modelWears?: string;
    atfStatement?: string;
    productDetails?: string;
  }>({});
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [swatches, setSwatches] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSizeSelectorOpen, setIsSizeSelectorOpen] = useState(false);
  const [combinedStoreInventory, setCombinedStoreInventory] = useState<Record<number, boolean> | null>(null);
  const galleryContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (galleryImages.length && galleryContainerRef.current) {
      (window as any).Alpine?.initTree(galleryContainerRef.current);
    }
  }, [galleryImages]);

  useEffect(() => {
    const handleOpen = async (event: Event) => {
      const { handle, title } = (
        event as CustomEvent<{ handle: string; title?: string }>
      ).detail;

      setProductHandle(handle);
      setLoadingTitle(title || "");
      setIsOpen(true);
      setIsLoading(true);
      setSelectedOptions({});
      setIsSizeSelectorOpen(false);
      setCombinedStoreInventory(null);
      setRestProduct(null);
      setGalleryImages([]);
      setMetafields({});
      setSwatches([]);

      try {
        const [restResponse, storefrontResponse] = await Promise.all([
          fetch(`/products/${handle}.js`),
          shopify.unauthenticatedFetch(`{
            product(handle: "${handle}") {
              descriptionHtml
              images(first: 50) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              metafields(identifiers: [
                {namespace: "ap21", key: "modelWears"},
                {namespace: "ap21", key: "atfStatement"},
                {namespace: "ap21", key: "productDetails"}
              ]) {
                namespace
                key
                value
              }
            }
          }`),
        ]);

        const productJson = await restResponse.json();
        setRestProduct(productJson);

        const variantIds = (productJson.variants || []).map((variant: any) => variant.id);
        if (variantIds.length) {
          shopify.getInventoryDetails(variantIds).then((response: any) => {
            const nodes = response?.data?.nodes || [];
            const hasCombinedStore = nodes.some((node: any) => {
              const inventoryLevels = node?.inventoryItem?.inventoryLevels?.nodes || [];
              return inventoryLevels.some((inventoryLevel: any) => inventoryLevel.location.name === "Combined Store");
            });
            if (!hasCombinedStore) return;
            const inventory: Record<number, boolean> = {};
            productJson.variants.forEach((variant: any, index: number) => {
              const node = nodes[index];
              const inventoryLevels = node?.inventoryItem?.inventoryLevels?.nodes || [];
              const combinedStore = inventoryLevels.find((inventoryLevel: any) => inventoryLevel.location.name === "Combined Store");
              inventory[variant.id] = !!(combinedStore && combinedStore.quantities[0]?.quantity > 0);
            });
            setCombinedStoreInventory(inventory);
          }).catch(() => {});
        }

        const storefrontProduct = storefrontResponse?.data?.product;
        const allImages: Array<{ url: string; alt: string }> = (
          storefrontProduct?.images?.edges || []
        )
          .map((edge: any) => ({
            url: edge.node.url as string,
            alt: (edge.node.altText as string) || productJson.title || "",
          }))
          .filter((image: { url: string; alt: string }) =>
            image.url && !image.url.match(/_s_|_s(\.|$)/i)
          );
        setGalleryImages(allImages);

        const metafieldNodes = storefrontProduct?.metafields || [];
        const metafieldsByKey: Record<string, string> = {};
        metafieldNodes.forEach((metafield: any) => {
          if (metafield) metafieldsByKey[metafield.key] = metafield.value;
        });
        setMetafields(metafieldsByKey);
        setDescriptionHtml(storefrontProduct?.descriptionHtml || "");

        if (productJson?.tags?.length > 0) {
          shopify
            .getRelatedProductsByTag(productJson, "colour:")
            .then((relatedProducts: any[]) => {
              if (!relatedProducts?.length) return;

              const getSwatchImage = (mediaList: any[], fallback: string): string => {
                const swatchMedia = mediaList?.find((media: any) => media.src?.match(/_s(\.|$)/i));
                return swatchMedia?.src || fallback || mediaList?.[mediaList.length - 1]?.src || "";
              };

              const colourSwatches = relatedProducts.map((relatedProduct: any) => ({
                url: `/products/${relatedProduct.handle}`,
                name: relatedProduct.title,
                image: getSwatchImage(relatedProduct.media || [], relatedProduct.featuredImage?.src || ""),
                is_product: relatedProduct.handle === handle,
                show_name: false,
                color: "",
              }));

              if (!colourSwatches.some((swatch: any) => swatch.is_product)) {
                const currentSwatchImage = getSwatchImage(
                  (productJson.images || []).map((image: any) => ({ src: image.src })),
                  productJson.images?.[0]?.src || ""
                );
                colourSwatches.unshift({
                  url: `/products/${handle}`,
                  name: productJson.title,
                  image: currentSwatchImage,
                  is_product: true,
                  show_name: false,
                  color: "",
                });
              }

              setSwatches(colourSwatches);
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error("[QuickView] Failed to load product data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener("quickview:open", handleOpen);
    return () =>
      window.removeEventListener("quickview:open", handleOpen);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open", "no-scroll");
    } else {
      document.body.classList.remove("modal-open", "no-scroll");
    }
    return () => document.body.classList.remove("modal-open", "no-scroll");
  }, [isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setRestProduct(null);
      setGalleryImages([]);
      setMetafields({});
      setSwatches([]);
      setSelectedOptions({});
      setCombinedStoreInventory(null);
      setProductHandle("");
    }, 300);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  const selectedVariant = useMemo(() => {
    if (!restProduct?.variants || !Object.keys(selectedOptions).length)
      return null;
    return (
      restProduct.variants.find((variant: any) =>
        (restProduct.options || []).every((option: any, optionIndex: number) => {
          const key = `option${optionIndex + 1}`;
          return !selectedOptions[option.name] || variant[key] === selectedOptions[option.name];
        })
      ) ?? null
    );
  }, [selectedOptions, restProduct]);

  const currentColor = useMemo(() => {
    if (!restProduct?.options?.length) return "";
    const colorOption = restProduct.options[0]?.name;
    if (!colorOption) return "";
    if (selectedOptions[colorOption]) return selectedOptions[colorOption];
    const firstAvailable = (restProduct.variants as any[]).find((variant: any) => variant.available);
    return (firstAvailable ?? restProduct.variants[0])?.option1 ?? "";
  }, [restProduct, selectedOptions]);

  const productTag = useMemo(() => {
    if (!restProduct?.tags) return null;
    const fitGroupTagPrefix = "Group_Fit:";
    const fitGroupTag = (restProduct.tags as string[]).find((tag: string) =>
      tag.includes(fitGroupTagPrefix)
    );
    if (!fitGroupTag) return null;
    return fitGroupTag.split(":").pop() || null;
  }, [restProduct]);

  const accordionItems = useMemo(() => {
    const rawDescription = metafields.productDetails || metafields.atfStatement || descriptionHtml || "";
    const descriptionText = rawDescription.replace(/<[^>]*>/g, "").trim();
    const descriptionItems = descriptionText
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean);
    const detailsContent = descriptionItems.length > 0
      ? `<ul class="list-disc">${descriptionItems.map((item) => `<li>${item}</li>`).join("")}</ul>`
      : rawDescription;
    const modelWears = metafields.modelWears
      ? `<br/><br/>Model wears ${metafields.modelWears}`
      : "";
    const items: Array<{ title: string; content: string }> = [
      {
        title: "Details",
        content: detailsContent + modelWears,
      },
      {
        title: settings.additional_info_2 || "Shipping & Returns",
        content: settings.additional_content_2 || SHIPPING_CONTENT,
      },
    ];
    if (settings.additional_info_3) {
      items.push({
        title: settings.additional_info_3,
        content: settings.additional_content_3 || "",
      });
    }
    return items;
  }, [metafields, descriptionHtml, settings]);

  const findInStoreSettings = useMemo(() => {
    if (!restProduct) return null;
    return {
      product: {
        title: restProduct.title,
        variants: restProduct.variants.map((variant: any) => ({
          id: variant.id,
          title: variant.title,
          option2: variant.option2 ?? null,
        })),
      },
    };
  }, [restProduct]);

  const learnMoreLabel = settings?.learn_more_label || "Learn More";

  if (!isOpen && !productHandle) return null;

  return (
    <div
      className={clsx(
        "quick-view-modal fixed inset-0 z-50 transition-opacity duration-300",
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      )}
    >
      <div className="absolute inset-0 bg-black/60" onClick={close} />

      <div 
        className="relative w-full h-full overflow-y-auto flex flex-col items-center justify-center" 
        onClick={event => {
          if (event.target === event.currentTarget) close();
        }}
      >
        <div className="relative bg-white w-[calc(100vw_-_30px)] lg:w-[calc(100vw_-_128px)] mx-auto my-0 lg:my-8 max-h-[calc(100vh_-_128px)] overflow-y-auto lg:px-16">

          {isLoading && (
            <div className="flex flex-col items-center justify-center h-[400px] lg:h-[600px]">
              {loadingTitle && (
                <p className="h4 mb-6 text-center px-8">{loadingTitle}</p>
              )}
              <div className="w-8 h-8 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
            </div>
          )}

          {!isLoading && restProduct && (
            <>
              <QuickViewContent
                productHandle={productHandle}
                close={close}
                galleryImages={galleryImages}
                galleryContainerRef={galleryContainerRef}
                isMobile={isMobile}
                restProduct={restProduct}
                productTag={productTag}
                metafields={metafields}
                descriptionHtml={descriptionHtml}
                swatches={swatches}
                currentColor={currentColor}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                selectedVariant={selectedVariant}
                findInStoreSettings={findInStoreSettings}
                isSizeSelectorOpen={isSizeSelectorOpen}
                setIsSizeSelectorOpen={setIsSizeSelectorOpen}
                accordionItems={accordionItems}
                learnMoreLabel={learnMoreLabel}
                combinedStoreInventory={combinedStoreInventory}
              />
              <div
                id="KiwiSizingChart"
                data-product={String(restProduct.id)}
                data-collections=""
                data-tags={(restProduct.tags ?? []).join(",")}
                data-vendor={restProduct.vendor ?? ""}
                data-product-name={restProduct.title}
                data-product-images={JSON.stringify(restProduct.images ?? [])}
                data-type={restProduct.product_type ?? ""}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
